<script lang="ts" setup>
import { ref, reactive, computed, onUnmounted, watch, inject, Ref } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { useAlerts } from '@/plugins/alerts';
import { useEvents } from '@/plugins/events';
import { Form } from '@/helpers/Form';
import { FormEntry, FormContract } from '@/components/forms/blueprints/logito-form';
import { FluentFormBuilder } from '@/components/forms';
import Loader from '@/components/common/Loader.vue';
import { DynamicModalType } from '@/components/forms/blueprints/logito-dynamic-modal/index';
import DynamicFormHelper from '@/modules/low-code/helpers/DynamicFormHelper';
import ModulesService from '@/modules/low-code/services/ModulesService';
import { useDynamicTrigger, runTrigger, emitBlueprintChanges } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerModel, TriggerModes, TriggerMoments } from '@/components/builder/form/traits/HasTriggers';
import { EntryFactory } from '@/components/builder/form/traits/EntryFactory';
import { FormBuilderContract } from '@/components/builder/form';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';

const props = defineProps({
  "endpointId": null,
  "baseBuilder": null,
  "licence": null,
  "blueprint": null,
  "currentBlueprint": null,
  "modalEntry": null,
  "tableRowIndex": null,
  "withValidation": { type: Boolean,  }
});

const { $t } = useLocalization();
const { $alert } = useAlerts();
const { $events } = useEvents();

const baseFormBuilder = inject<Ref<FormBuilderContract>>('baseFormBuilder');

const form = reactive(Form.create({
    document: null as FormContract,
}));

const formObj = ref<HTMLElement | null>(null);
const entry = ref<FormEntry>(new FormEntry());
const builder = ref(
    FluentFormBuilder.setup(false, true)
        .blueprint(
            () => form.document,
            () => 0
        )
        .entry(
            () => entry.value,
            () => 0
        )
        .make()
);

const level = inject<number>('level', 0);
const isLoaded = ref(false);
const reloadKey = ref(0);
const formId = computed(() => props.blueprint.dynamicForm.key);

watch(
    () => formObj?.value,
    async (val) =>
    {
        if (val)
        {
            await new Promise(resolve => setTimeout(resolve, 200));
            await runTrigger(TriggerMoments.onLoad, form.document, null, builder.value, baseFormBuilder.value, props.licence, props.endpointId, reloadDataWithoutReloadForm, $alert, entry.value, formObj, props, $events);
        }
    },
    { once: true }
);

const loadForm = async () =>
{
    try
    {
        let url = `bpmn-start/${props.endpointId}`;

        if (props.endpointId.includes('/'))
        {
            const [docId, endpointId] = props.endpointId.split('/');

            url = `${docId}/bpmn-execute/${endpointId}`;
        }

        const response = await ModulesService.fetchForm(`${props.licence}-dynamic-modal-${formId.value}/${url}`);

        form.document = response;
        builder.value.update();
    }
    catch (ex: any)
    {
        if (ex.code === 400) $alert.warning(ex.message);
    }
};

const loadData = async () =>
{
    if (props.blueprint.script?.key)
        await executeScript();
};

const addEvents = async () =>
{
    $events.$on(`form-reload-data-${builder.value.blueprint.id}`, (data: any) =>
    {
        reloadData(data);
        reloadKey.value++;
    });

    const fullLicence = `${props.licence}-dynamic-modal-${formId.value}`;

    useDynamicTrigger($events, builder.value.blueprint.id).eventOn(builder.value, baseFormBuilder.value, fullLicence, props.endpointId, reloadDataWithoutReloadForm, $alert, entry.value, formObj, props);
};

const executeScript = async () =>
{
    form.wait();

    try
    {
        const response =
            await DynamicFormHelper.getFormData(
                props.licence, props.endpointId, props.modalEntry, props.baseBuilder, props.baseBuilder, props.blueprint.script.key, props.blueprint.scriptConfigId, props.currentBlueprint, props.blueprint.dynamicForm.key, props.tableRowIndex, props.withValidation ?? false);

        Object.entries(response).forEach(([key, value]) =>
        {
            const currentBlueprint = builder.value.schema.find(key) as EntryFactory<any>;

            if (!currentBlueprint) return;

            const source = (value as any).data ?? value;

            entry.value[key] = currentBlueprint.createEntry(source);
        });

        builder.value.update();
    }
    catch (ex: any)
    {
        if (ex.code === 422)
        {
            const errors: Record<string, string[]> = {};

            Object.entries(ex.data.errors).forEach(([key, value]) =>
            {
                const keyWithDot =
                    key.includes('.value') || key.includes('.custom')
                        ? key.toLowerCase()
                        : `${key.toLowerCase()}.value`;

                errors[keyWithDot] = value as string[];
            });

            builder.value.setEntryErrors(errors);
            $alert.warning($t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]'));
        }
    }
    finally
    {
        form.continue();
    }
};

const addEntriesData = () =>
{
    entry.value = builder.value.getEntry() as FormEntry;

    entry.value.module = props.licence + '-dynamic-modal-' + formId.value;
    entry.value.formId = props.endpointId;
    entry.value.actionName = props.endpointId.includes('/') ? 'bpmn-execute' : 'bpmn-start';
};

const initForm = async () =>
{
    form.wait();

    await loadForm();
    addEntriesData();
    await loadData();
    addEvents();

    form.complete();
};

const reloadData = async (data: any, blueprint?: any, tableRowIndex?: number | null) =>
{
    Object.keys(data).forEach((key) =>
    {
        if (entry.value.hasOwnProperty(key))
        {
            const currentBlueprint = builder.value.schema.find(key) as EntryFactory<any>;

            if (!currentBlueprint) return;

            entry.value[key] = currentBlueprint.createEntry(data[key].data);
            // entry.value[key] = data[key];
        }
    });

    builder.value.update();
    reloadKey.value++;
};

const reloadDataWithoutReloadForm = async (data: any, blueprint?: any, tableRowIndex?: number | null) =>
{
    Object.keys(data).forEach((key) =>
    {
        if (entry.value.hasOwnProperty(key))
        {
            const currentBlueprint = builder.value.schema.find(key) as EntryFactory<any>;

            if (!currentBlueprint) return;

            const oldEntry = JSON.parse(JSON.stringify(entry.value[key]));

            entry.value[key] = currentBlueprint.updateEntry(data[key].data, oldEntry);
            useControlReload($events, key, level).emitReload(tableRowIndex);

            emitBlueprintChanges($events, builder.value.blueprint.id, blueprint, currentBlueprint, entry.value[key], oldEntry, tableRowIndex);
        }
    });
};

const init = async () =>
{
    await initForm();

    isLoaded.value = true;
};

init();


onUnmounted(() =>
{
    $events.$off(`form-reload-data-${builder.value.blueprint.id}`);
    useDynamicTrigger($events, builder.value.blueprint.id).eventOff();
});

const getBuilder = async () =>
{
    return builder.value;
};

defineExpose({
    getBuilder
});
</script>

<template>
    <DataCard>
        <template #default>
            <div v-if="!isLoaded" class="position-absolute top-50 start-50 translate-middle">
                <Loader />
            </div>
            <div v-else class="row">
                <div class="col-md-12">
                    <form id="dynamic-form">
                        <FormBlueprint :key="reloadKey" :builder="builder" />
                    </form>
                </div>
            </div>
        </template>
    </DataCard>
</template>
